// File: src/theme.js
//
// Copyright (c) 2025 DefoAI UG (haftungsbeschränkt)
// All rights reserved.
//
/**
 * @file This new file defines the custom Material-UI theme for the application.
 *
 * @description NEW:
 * A centralized theme provides a consistent and polished visual identity. By
 * defining colors, typography, and component styles in one place, we can
 * ensure the entire admin portal looks and feels like a cohesive, modern
 * product. This approach makes future design updates much easier to implement.
 *
 * Key Theming Decisions:
 * - **Color Palette:** A professional and accessible color scheme with a
 * calm blue as the primary color and a clean, light background.
 * - **Typography:** Standardizes the "Inter" font (already imported in App.js)
 * across the application for a highly readable and modern text style.
 * - **Component Overrides:** Subtle adjustments to default component styles
 * (e.g., buttons, cards) create a softer and more user-friendly aesthetic.
 * For example, buttons no longer use aggressive all-caps text.
 * - **Shape:** A consistent border radius is applied to elements like buttons
 * and cards to create a unified and contemporary visual language.
 */

import { createTheme } from '@mui/material/styles';

// Create and export the custom theme for the application.
const theme = createTheme({
    // Define the color palette.
    palette: {
        mode: 'light',
        primary: {
            // A professional, accessible blue for primary actions and highlights.
            main: '#1976d2',
        },
        secondary: {
            // A distinct color for secondary actions.
            main: '#9c27b0',
        },
        background: {
            // A light grey for the main content background, reducing eye strain.
            default: '#f4f6f8',
            // Pure white for elevated surfaces like cards and menus.
            paper: '#ffffff',
        },
        text: {
            primary: '#172b4d',
            secondary: '#6b778c',
        },
    },

    // Define typography settings to ensure consistent text styling.
    typography: {
        // Set "Inter" as the primary font family for a clean, modern look.
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif",
        h6: {
            fontWeight: 600, // Make h6 (used in headers) slightly bolder.
        },
    },

    // Define the global shape of components.
    shape: {
        // Apply a consistent, slightly rounded corner to most components.
        borderRadius: 8,
    },

    // Define global style overrides for specific Material-UI components.
    components: {
        // Customizations for the Button component.
        MuiButton: {
            styleOverrides: {
                root: {
                    // Disable the default all-caps transformation for a less formal UI.
                    textTransform: 'none',
                },
            },
        },
        // Customizations for the Card component.
        MuiCard: {
            styleOverrides: {
                root: {
                    // Use a more subtle box-shadow for a lighter, modern elevation effect.
                    boxShadow: 'rgba(149, 157, 165, 0.1) 0px 8px 24px',
                    // Ensure cards use the consistent border radius.
                    borderRadius: 8,
                },
            },
        },
        // Customizations for the Paper component.
        MuiPaper: {
            styleOverrides: {
                root: {
                     // Apply the same subtle shadow to all Paper components.
                    boxShadow: 'rgba(149, 157, 165, 0.1) 0px 8px 24px',
                }
            }
        },
        // Customizations for Table Cells.
        MuiTableCell: {
            styleOverrides: {
                head: {
                    // Make table headers clearer with a distinct color and weight.
                    color: '#6b778c', // text.secondary
                    fontWeight: 600,
                },
            },
        },
    },
});

export default theme;


